import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiErrorDetail, friendlyError } from "../lib/api";
import { Lock, Mail } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Vítej zpět, ${u.name.split(" ")[0]}!`);
      const dest = location.state?.from || (u.is_admin ? "/admin" : "/");
      navigate(dest);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper py-10 px-4 grid place-items-center">
      <div className="w-full max-w-md">
        <Link to="/" className="font-heading font-extrabold text-2xl tracking-tight text-slate-900 flex items-center gap-0.5 mb-8 justify-center" data-testid="login-back-logo">
          ConnectYou<span className="text-sky-500">.</span>
        </Link>

        <div className="bg-white rounded-3xl border border-slate-200 p-7 sm:p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Přihlášení</h1>
          <p className="text-slate-500 mt-1 font-medium">Vítej zpátky.</p>

          <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-bold text-slate-700 mb-1.5">
                <Mail className="w-4 h-4 text-slate-400" /> E-mail
              </label>
              <input
                data-testid="login-email-input"
                type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium"
              />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-sm font-bold text-slate-700 mb-1.5">
                <Lock className="w-4 h-4 text-slate-400" /> Heslo
              </label>
              <input
                data-testid="login-password-input"
                type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-sky-400 focus:ring-4 focus:ring-sky-100 outline-none font-medium"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-xl bg-red-50 text-red-700 text-sm font-semibold p-3">{error}</div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit" disabled={submitting}
              className="px-6 py-3 rounded-full bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold transition-all active:scale-95 shadow-[0_12px_30px_-10px_rgba(14,165,233,0.55)]"
            >
              {submitting ? "Přihlašuji…" : "Přihlásit se"}
            </button>

            <p className="text-center text-sm text-slate-500 mt-2">
              Nemáš účet?{" "}
              <Link data-testid="login-go-register" to="/register" className="text-sky-600 font-bold underline underline-offset-4">Zaregistruj se</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
